import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { publicFileUrl } from "@/lib/utils-app";
import { webOrigin } from "@/lib/nativeBridge";
import PublicShell from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import BookingDialog from "@/components/BookingDialog";
import SchedulePicker from "@/components/SchedulePicker";
import StarRating from "@/components/StarRating";
import { toast } from "sonner";
import { ShieldCheckIcon as ShieldCheck, GraduationCapIcon as GraduationCap, ScissorsIcon as Scissors, FootprintsIcon as Footprints, HouseIcon as House, MapPinIcon as MapPin, PhoneIcon as Phone, GlobeIcon as Globe, ShareIcon as Share, CheckCircleIcon as CheckCircle, WarningIcon as Warning, ChatCircleIcon as ChatCircle, VideoCameraIcon as VideoCamera, FilmStripIcon as FilmStrip, ClockIcon as Clock, SparkleIcon as Sparkle, TreeIcon as Tree } from "@phosphor-icons/react";

const KIND_META = {
  trainer: { label: "Trainer", Icon: GraduationCap },
  groomer: { label: "Groomer", Icon: Scissors },
  walker:  { label: "Walker",  Icon: Footprints },
  sitter:  { label: "Sitter",  Icon: House },
  field_host: { label: "Zoomie Time Host", Icon: Tree },
  other:   { label: "Specialty Pro", Icon: Sparkle },
};

/**
 * `/provider/{slug}` — public provider profile page.
 *
 * Designed for both anonymous visitors and logged-in users. The
 * "Request booking" CTA below is a Phase-1 placeholder (toasts that
 * the real booking flow lands in Phase 2). That keeps the page
 * complete and shareable from day 1 without us pretending the
 * booking calendar exists yet.
 */
export default function PublicProvider() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [msgBusy, setMsgBusy] = useState(false);
  const [sessionOffering, setSessionOffering] = useState(null); // {available, tiers, ...}
  const [courses, setCourses] = useState([]);
  const [schedFor, setSchedFor] = useState(null); // {tierIndex, label, duration_min}

  useEffect(() => {
    api.get(`/public/providers/${slug}`)
      .then((r) => setProvider(r.data))
      .catch((e) => setErr(e?.response?.status === 404 ? "not-found" : "error"));
    api.get(`/public/providers/${slug}/reviews`)
      .then((r) => setReviews(r.data || []))
      .catch(() => setReviews([]));
  }, [slug]);

  // Once we know the provider's id + kind, pull trainer-learning data.
  // Both endpoints gracefully no-op for non-trainer providers (404/empty).
  useEffect(() => {
    if (!provider?.id) return;
    const kind = provider.kind;
    if (kind !== "trainer" && kind !== "behaviorist") return;
    api.get(`/trainer-sessions/by-provider/${provider.id}`)
      .then((r) => setSessionOffering(r.data))
      .catch(() => setSessionOffering(null));
    api.get(`/trainer-courses?provider_id=${provider.id}&limit=24`)
      .then((r) => setCourses(r.data?.courses || []))
      .catch(() => setCourses([]));
  }, [provider?.id, provider?.kind]);

  const share = async () => {
    const url = `${webOrigin()}${window.location.pathname}`;
    try {
      if (navigator.share) await navigator.share({ url, title: provider?.business_name });
      else await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {/* user dismissed */ }
  };

  const startMessage = async () => {
    if (!provider?.id) return;
    setMsgBusy(true);
    try {
      // Wires into the Provider ↔ Client direct-messaging system shipped 2026-06-05.
      // Idempotent: returns the existing thread if one already exists.
      const r = await api.post("/provider-threads/start", { provider_id: provider.id });
      navigate(`/inbox/threads/${r.data.id}`);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Please sign in to message this provider");
        navigate("/login?next=" + encodeURIComponent(window.location.pathname));
      } else if (e?.response?.status === 400) {
        toast.error(e?.response?.data?.detail || "You can't message your own provider profile.");
      } else {
        toast.error(e?.response?.data?.detail || "Could not start conversation");
      }
    } finally {
      setMsgBusy(false);
    }
  };

  const bookTrainerSession = (tierIndex) => {
    if (!provider?.id) return;
    const tier = sessionOffering?.tiers?.[tierIndex];
    setSchedFor({
      tierIndex,
      label: tier?.label || "Live session",
      duration_min: tier?.duration_min || 45,
    });
  };

  const submitBooking = async (isoUtc) => {
    if (!schedFor || !provider?.id) return;
    try {
      const r = await api.post("/trainer-sessions/book", {
        provider_id: provider.id,
        tier_index: schedFor.tierIndex,
        scheduled_at: isoUtc,
      });
      setSchedFor(null);
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.success("Session booked.");
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Please sign in to book this session");
        navigate("/login?next=" + encodeURIComponent(window.location.pathname));
      } else {
        toast.error(e?.response?.data?.detail || "Could not book session");
      }
    }
  };

  if (err === "not-found") {
    return (
      <PublicShell>
        <div className="max-w-2xl mx-auto py-12 text-center">
          <h1 className="font-spectral text-3xl">Provider not found</h1>
          <p className="text-sm text-[#6B6B6B] mt-3">This profile may be pending review or no longer listed.</p>
        </div>
      </PublicShell>
    );
  }
  if (!provider) {
    return <PublicShell><div className="text-[#6B6B6B]">Loading…</div></PublicShell>;
  }

  const meta = KIND_META[provider.kind] || KIND_META.trainer;
  const Icon = meta.Icon;
  const kindLabel = provider.specialty_label || meta.label;

  return (
    <PublicShell>
      <Helmet>
        <title>{`${provider.business_name} · ${kindLabel} on Armstrong K9 Registry`}</title>
        <meta name="description" content={provider.tagline || `${kindLabel} in ${provider.location?.city}, ${provider.location?.state}`} />
        <meta property="og:title" content={`${provider.business_name} · ${kindLabel}`} />
        <meta property="og:description" content={provider.tagline || ""} />
        {provider.photo_url && <meta property="og:image" content={publicFileUrl(provider.photo_url)} />}
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="bg-ink text-white rounded-2xl p-6 md:p-10 relative overflow-hidden" data-testid="provider-hero">
          <div
            aria-hidden
            className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-gold/15 blur-3xl pointer-events-none"
          />
          <div className="relative flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex items-start gap-4 sm:gap-5 flex-1 min-w-0">
              <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full overflow-hidden bg-white/10 ring-1 ring-gold/40 flex items-center justify-center flex-shrink-0">
                {provider.photo_url ? (
                  <img src={publicFileUrl(provider.photo_url)} alt={provider.business_name} className="w-full h-full object-cover" />
                ) : (
                  <Icon size={40} weight="duotone" className="text-gold-light" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-light mb-3 flex items-center gap-1.5">
                  <Icon size={11} weight="duotone" />
                  Independent {kindLabel}
                </p>
                <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight leading-[1.05] flex items-center gap-3 flex-wrap" data-testid="provider-name">
                  {provider.logo_url && (
                    <img
                      src={publicFileUrl(provider.logo_url)}
                      alt={`${provider.business_name} logo`}
                      className="h-10 w-10 rounded-md object-contain bg-white/95 p-1 ring-1 ring-gold/40 flex-shrink-0"
                      data-testid="provider-logo-badge"
                    />
                  )}
                  {provider.business_name}
                  {provider.insurance_verified ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-[#0F8043] text-white" data-testid="insurance-verified-badge">
                      <ShieldCheck size={10} weight="fill" /> Insurance verified
                    </span>
                  ) : provider.has_insurance ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-white/15 text-white border border-white/25">
                      <ShieldCheck size={10} weight="fill" /> Insured (self-reported)
                    </span>
                  ) : null}
                  {provider.background_check_status === "clear" && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-[#0F8043] text-white" data-testid="bg-check-clear-badge">
                      <ShieldCheck size={10} weight="fill" /> Background check cleared
                    </span>
                  )}
                </h1>
                {provider.rating?.count > 0 && (
                  <div className="mt-3 inline-flex items-center gap-2 text-white/85 text-sm" data-testid="provider-rating">
                    <StarRating value={provider.rating.avg} size={14} />
                    <span className="font-tabular font-semibold">{provider.rating.avg.toFixed(1)}</span>
                    <span className="text-white/65">· {provider.rating.count} review{provider.rating.count === 1 ? "" : "s"}</span>
                  </div>
                )}
                {provider.tagline && <p className="text-white/80 mt-3 text-lg">{provider.tagline}</p>}
                <div className="gold-hairline mt-4 mb-4 max-w-md opacity-60" />
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/80">
                  <span className="inline-flex items-center gap-1.5"><MapPin size={12} weight="duotone" /> {provider.location?.city}, {provider.location?.state} {provider.location?.zip}{provider.kind === "field_host" ? " (approximate area)" : ""}</span>
                  {provider.phone && <span className="inline-flex items-center gap-1.5"><Phone size={12} weight="duotone" /> {provider.phone}</span>}
                  {provider.website_url && <a href={provider.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-gold-light hover:underline"><Globe size={12} weight="duotone" /> Website</a>}
                </div>
                {(() => {
                  const WELCOMES = {
                    meds_ok: "Administers medications", puppies_ok: "Puppies welcome",
                    seniors_ok: "Senior dogs welcome", reactive_ok: "Reactive dogs welcome",
                    aggression_ok: "Aggression cases accepted", matted_coats_ok: "Matted coats OK",
                    bite_history_ok: "Bite-history dogs OK", giant_breeds_ok: "Giant breeds (80+ lb) OK",
                    in_heat_ok: "Dogs in heat welcome",
                  };
                  const chips = Object.entries(WELCOMES).filter(([k]) => provider.care_prefs?.[k] === true);
                  if (!chips.length) return null;
                  return (
                    <div className="flex flex-wrap gap-2 mt-4" data-testid="provider-welcomes">
                      {chips.map(([k, label]) => (
                        <span key={k} className="text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 bg-gold/20 text-gold-light border border-gold/30">
                          {label}
                        </span>
                      ))}
                    </div>
                  );
                })()}
                {provider.kind === "field_host" && provider.land && (
                  <div className="mt-5 bg-white/10 border border-white/20 rounded-xl p-4 max-w-2xl" data-testid="land-details-card">
                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold-light mb-2.5 flex items-center gap-1.5">
                      <Tree size={12} weight="duotone" /> The field
                    </p>
                    <div className="flex flex-wrap gap-2 mb-2.5">
                      <span className="text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 bg-white/15 text-white">{provider.land.acres} acres</span>
                      <span className="text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 bg-white/15 text-white">
                        {provider.land.fenced === "fully" ? `Fully fenced${provider.land.fence_height_ft ? ` · ${provider.land.fence_height_ft} ft` : ""}` : provider.land.fenced === "partially" ? "Partially fenced" : "Not fenced"}
                      </span>
                      <span className="text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 bg-white/15 text-white">Max {provider.land.max_dogs} dogs</span>
                      {provider.land.water_access && <span className="text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 bg-white/15 text-white">Water for dogs</span>}
                      {provider.land.agility_equipment && <span className="text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 bg-white/15 text-white">Agility equipment</span>}
                      {provider.land.parking_onsite && <span className="text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 bg-white/15 text-white">On-site parking</span>}
                    </div>
                    {provider.land.terrain && <p className="text-[13px] text-white/80 leading-relaxed"><strong className="text-white">Terrain:</strong> {provider.land.terrain}</p>}
                    {provider.land.hazards_note && <p className="text-[13px] text-white/80 leading-relaxed mt-1"><strong className="text-white">Heads up:</strong> {provider.land.hazards_note}</p>}
                    <p className="text-[11px] text-white/60 leading-relaxed mt-2.5">
                      Visits are private — the host leaves a {provider.land.buffer_minutes || 30}-minute gap between guests. The exact address is shared after your booking is confirmed. All field details are self-reported by the host and not verified by Armstrong K9 Registry.
                    </p>
                  </div>
                )}
                {provider.bio && (
                  <p className="mt-5 text-white/85 leading-relaxed max-w-2xl whitespace-pre-wrap">
                    {provider.bio}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-row md:flex-col gap-2 md:self-start flex-shrink-0 flex-wrap">
              <Button
                onClick={() => {
                  if (!provider.services || provider.services.length === 0) {
                    toast.info("This provider hasn't published bookable services yet. Use Share to send them a note.");
                    return;
                  }
                  setBookOpen(true);
                }}
                className="rounded-full bg-gold-gradient hover:opacity-95 text-ink font-semibold border-0 shadow-[0_8px_24px_rgba(201,162,39,0.35)]"
                data-testid="provider-book-btn"
              >
                Request booking
              </Button>
              <Button
                onClick={startMessage}
                disabled={msgBusy || !provider.id}
                variant="outline"
                className="rounded-full border-white/25 bg-white/10 hover:bg-white/15 text-white"
                data-testid="provider-message-btn"
              >
                <ChatCircle size={14} className="mr-1" weight="duotone" /> {msgBusy ? "Opening…" : "Message"}
              </Button>
              <Button
                onClick={share}
                variant="outline"
                className="rounded-full border-white/20 bg-white/10 hover:bg-white/15 text-white"
                data-testid="provider-share-btn"
              >
                {copied ? <><CheckCircle size={14} className="mr-1" weight="fill" /> Copied</> : <><Share size={14} className="mr-1" /> Share</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Portfolio gallery */}
        {Array.isArray(provider.gallery) && provider.gallery.length > 0 && (
          <div className="mt-10" data-testid="provider-public-gallery">
            <h2 className="font-spectral text-3xl tracking-tight mb-4">Portfolio</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {provider.gallery.map((item, idx) => (
                <figure
                  key={`${item.url}-${idx}`}
                  className="bg-white border border-[#EAEAEA] rounded-xl overflow-hidden group"
                  data-testid={`provider-public-gallery-item-${idx}`}
                >
                  <img
                    src={publicFileUrl(item.url)}
                    alt={item.caption || `${provider.business_name} portfolio ${idx + 1}`}
                    className="w-full h-44 object-cover group-hover:scale-[1.02] transition-transform"
                    loading="lazy"
                  />
                  {item.caption && (
                    <figcaption className="px-3 py-2 text-[12px] text-[#3A3A3A] leading-snug">
                      {item.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        )}

        {/* Trainer Learning — sessions + courses widget. Only renders
            for trainer/behaviorist providers who have actually
            published a session offering or at least one course. */}
        {(provider.kind === "trainer" || provider.kind === "behaviorist") &&
          ((sessionOffering?.available && (sessionOffering?.tiers || []).length > 0) || courses.length > 0) && (
          <div className="mt-10 grid lg:grid-cols-2 gap-4" data-testid="provider-learning-block">
            {sessionOffering?.available && (sessionOffering?.tiers || []).length > 0 && (
              <div className="bg-white border border-[#EAEAEA] rounded-2xl p-5" data-testid="provider-live-sessions">
                <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-2">
                  <VideoCamera size={20} weight="duotone" className="text-[#C1272D]" />
                  Live 1-on-1 sessions
                </h2>
                <p className="text-[12.5px] text-[#6B6B6B] mt-1">
                  Book a private video session with {provider.business_name}. Recording included for 30 days.
                </p>
                {sessionOffering.first_session_free && (
                  <p className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-gold/15 text-[#7a5a00] mt-2">
                    First session free for new buyers
                  </p>
                )}
                <ul className="mt-4 space-y-2" data-testid="provider-session-tiers">
                  {sessionOffering.tiers.map((t, i) => (
                    <li key={i} className="border border-[#EFEFEF] rounded-xl p-3 flex items-center gap-3" data-testid={`provider-tier-${i}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold">{t.label}</p>
                        <p className="text-xs text-[#666] flex items-center gap-2">
                          <span>{t.sessions} session{t.sessions === 1 ? "" : "s"}</span>
                          <span className="inline-flex items-center gap-1"><Clock size={11} /> {t.duration_min || 45} min ea.</span>
                        </p>
                      </div>
                      <span className="font-tabular font-semibold whitespace-nowrap">${((t.price_cents || 0) / 100).toFixed(0)}</span>
                      <Button
                        size="sm"
                        onClick={() => bookTrainerSession(i)}
                        className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white text-[12px] h-8"
                        data-testid={`provider-book-tier-${i}`}
                      >
                        Book
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {courses.length > 0 && (
              <div className="bg-white border border-[#EAEAEA] rounded-2xl p-5" data-testid="provider-courses">
                <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-2">
                  <FilmStrip size={20} weight="duotone" className="text-[#C1272D]" />
                  Video courses
                </h2>
                <p className="text-[12.5px] text-[#6B6B6B] mt-1">
                  Pre-recorded lessons from {provider.business_name}. Buy once, watch forever.
                </p>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="provider-courses-list">
                  {courses.slice(0, 6).map((c) => (
                    <li key={c.id}>
                      <Link
                        to={`/courses/${c.slug}`}
                        className="block border border-[#EFEFEF] rounded-xl overflow-hidden hover:border-gold/55 transition-colors"
                        data-testid={`provider-course-${c.slug}`}
                      >
                        {c.cover_url ? (
                          <img src={c.cover_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${c.cover_url}` : c.cover_url} alt={c.title} className="w-full aspect-video object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full aspect-video bg-gradient-to-br from-[#0E0E0E] to-[#1f1f1f] grid place-items-center">
                            <GraduationCap size={32} className="text-gold" />
                          </div>
                        )}
                        <div className="p-3">
                          <p className="font-semibold text-[13.5px] truncate">{c.title}</p>
                          <p className="text-[11.5px] text-[#666] mt-0.5">{c.lesson_count} lesson{c.lesson_count === 1 ? "" : "s"} · ${((c.flat_price_cents || 0) / 100).toFixed(0)}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
                {courses.length > 6 && (
                  <Link to="/courses" className="inline-block mt-3 text-xs underline text-[#666]" data-testid="provider-courses-see-all">
                    See all {courses.length} courses →
                  </Link>
                )}
              </div>
            )}
          </div>
        )}

        {/* Trainer transparency disclosures — methods, tools, sworn
            consumer attestations. Only renders for trainer/behaviorist
            providers who've published anything in this section. */}
        {(provider.kind === "trainer" || provider.kind === "behaviorist") &&
          ((provider.training_methods?.length || 0) > 0 ||
            (provider.tools_used?.length || 0) > 0 ||
            provider.disclosures_detail ||
            typeof provider.years_experience === "number") && (
          <div className="mt-10 bg-white border border-[#EAEAEA] rounded-2xl p-5" data-testid="provider-disclosures-block">
            <p className="text-xs uppercase tracking-[0.18em] text-gold font-bold">Transparency</p>
            <h2 className="font-spectral text-2xl tracking-tight mt-1">Training methods &amp; sworn disclosures</h2>
            <p className="text-[12px] text-[#888] mt-1 max-w-2xl">
              Self-attested by this trainer. Armstrong K9 Registry does not independently verify these statements.
            </p>
            <div className="mt-4 grid sm:grid-cols-2 gap-4 text-[13px]">
              {typeof provider.years_experience === "number" && (
                <DisclRow label="Years training" value={`${provider.years_experience} year${provider.years_experience === 1 ? "" : "s"}`} />
              )}
              {provider.liability_insurance_held !== undefined && (
                <DisclRow label="Liability insurance" value={provider.liability_insurance_held ? "Carried" : "Not held"} />
              )}
              {provider.disclosure_no_animal_cruelty_history !== undefined && (
                <DisclRow label="Animal-cruelty history" value={provider.disclosure_no_animal_cruelty_history ? "None disclosed" : "See note below"} />
              )}
              {provider.disclosure_no_open_consumer_complaints !== undefined && (
                <DisclRow label="Open consumer complaints" value={provider.disclosure_no_open_consumer_complaints ? "None disclosed" : "See note below"} />
              )}
            </div>
            {(provider.training_methods?.length || 0) > 0 && (
              <div className="mt-4">
                <p className="text-[11px] uppercase tracking-wider text-[#888] mb-1.5">Methods used</p>
                <div className="flex flex-wrap gap-1.5" data-testid="provider-methods">
                  {provider.training_methods.map((m) => (
                    <span key={m} className="rounded-full bg-[#FBF7EC] border border-gold/40 text-[#7a5a00] text-[11.5px] font-semibold px-2.5 py-0.5">{labelize(m)}</span>
                  ))}
                </div>
              </div>
            )}
            {(provider.tools_used?.length || 0) > 0 && (
              <div className="mt-4">
                <p className="text-[11px] uppercase tracking-wider text-[#888] mb-1.5">Tools used</p>
                <div className="flex flex-wrap gap-1.5" data-testid="provider-tools">
                  {provider.tools_used.map((t) => (
                    <span key={t} className="rounded-full bg-[#F4F4F4] border border-[#E5E5E5] text-[#3F3F3F] text-[11.5px] px-2.5 py-0.5">{labelize(t)}</span>
                  ))}
                </div>
              </div>
            )}
            {provider.disclosures_detail && (
              <div className="mt-4">
                <p className="text-[11px] uppercase tracking-wider text-[#888] mb-1.5">Additional disclosures</p>
                <p className="text-[13px] text-[#3F3F3F] whitespace-pre-wrap" data-testid="provider-disclosures-detail">{provider.disclosures_detail}</p>
              </div>
            )}
          </div>
        )}

        {/* Service catalog */}
        <div className="mt-10">
          <h2 className="font-spectral text-3xl tracking-tight mb-4">Services</h2>
          {(provider.services || []).length === 0 ? (
            <div className="bg-white border border-[#EAEAEA] rounded-xl p-6 text-sm text-[#6B6B6B]" data-testid="provider-services-empty">
              {provider.business_name} hasn't published a service catalog yet. Check back soon or use the share button to send them a note.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {provider.services.map((s) => (
                <div key={s.id} className="bg-white border border-[#EAEAEA] rounded-xl overflow-hidden hover:border-gold/55 transition-colors" data-testid={`service-${s.id}`}>
                  {Array.isArray(s.photos) && s.photos.length > 0 && (
                    <div className={s.photos.length === 1 ? "" : "grid grid-cols-3 gap-px bg-[#EAEAEA]"}>
                      {s.photos.slice(0, 3).map((p, i) => (
                        <img
                          key={`${p}-${i}`}
                          src={publicFileUrl(p)}
                          alt={`${s.name} ${i + 1}`}
                          className={`object-cover ${s.photos.length === 1 ? "w-full h-40" : "w-full h-24"}`}
                          loading="lazy"
                          data-testid={`service-${s.id}-photo-${i}`}
                        />
                      ))}
                    </div>
                  )}
                  <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-ink">{s.name}</p>
                      <p className="text-[12px] text-[#6B6B6B] mt-0.5">
                        {s.duration_minutes} min · {s.kind === "recurring" ? "Recurring" : "One-time"}
                        {s.virtual ? " · Video call" : ""}
                      </p>
                    </div>
                    <span className="font-tabular font-semibold text-ink whitespace-nowrap">${s.price.toFixed(2)}</span>
                  </div>
                  {s.description && <p className="text-[12.5px] text-[#6B6B6B] mt-2 leading-relaxed">{s.description}</p>}
                  <Button
                    size="sm"
                    onClick={() => setBookOpen(true)}
                    className="mt-3 rounded-full bg-ink hover:bg-crimson text-white text-[12px] h-8"
                    data-testid={`service-book-${s.id}`}
                  >
                    Book this service
                  </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reviews */}
        <div className="mt-10" data-testid="provider-reviews">
          <h2 className="font-spectral text-3xl tracking-tight mb-4 flex items-center gap-3">
            Reviews
            {provider.rating?.count > 0 && (
              <span className="text-sm font-normal text-[#6B6B6B] inline-flex items-center gap-1.5">
                <StarRating value={provider.rating.avg} size={12} />
                <span className="font-tabular">{provider.rating.avg.toFixed(1)} · {provider.rating.count}</span>
              </span>
            )}
          </h2>
          {reviews.length === 0 ? (
            <div className="bg-white border border-[#EAEAEA] rounded-xl p-6 text-sm text-[#6B6B6B]" data-testid="reviews-empty">
              No reviews yet. Be the first — book this provider and share your experience after.
            </div>
          ) : (
            <div className="space-y-3">
              {reviews.map((r) => (
                <div key={r.id} className="bg-white border border-[#EAEAEA] rounded-xl p-4" data-testid={`review-${r.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <StarRating value={r.rating} size={13} />
                    <span className="text-[12.5px] font-semibold text-ink">{r.customer_name}</span>
                    {r.service_name && <span className="text-[11.5px] text-[#6B6B6B]">· {r.service_name}</span>}
                    <span className="text-[11px] text-[#9A9A9A] ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  {r.text && <p className="text-[13px] text-ink leading-relaxed whitespace-pre-wrap">{r.text}</p>}
                  {r.response?.text && (
                    <div className="mt-3 ml-4 pl-3 border-l-2 border-gold/40">
                      <p className="text-[11px] uppercase tracking-wider font-bold text-gold mb-1">Response from {provider.business_name}</p>
                      <p className="text-[12.5px] text-[#3A3A3A] leading-relaxed whitespace-pre-wrap">{r.response.text}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Trust & venue disclosures */}
        <div className="mt-10 pt-8 border-t border-[#EAEAEA] grid md:grid-cols-2 gap-6 text-[12.5px] text-[#6B6B6B] leading-relaxed">
          <div>
            <p className="font-semibold text-ink text-[13px] flex items-center gap-1.5 mb-1.5">
              <ShieldCheck size={13} weight="duotone" className="text-[#0F8043]" /> Insurance
            </p>
            {provider.insurance_verified ? (
              <p>
                {provider.business_name}'s insurance Certificate of Coverage has been <strong className="text-ink">reviewed and verified by Armstrong</strong>
                {provider.insurance_expires_at ? `, valid through ${provider.insurance_expires_at.slice(0, 10)}` : ""}.
                Armstrong does not guarantee ongoing policy validity — re-confirm with the provider as needed.
              </p>
            ) : provider.has_insurance ? (
              <p>
                {provider.business_name} has a current insurance policy on file
                {provider.insurance_expires_at ? ` valid through ${provider.insurance_expires_at.slice(0, 10)}` : ""}.
                Insurance information is <strong className="text-ink">self-reported by the provider</strong> and has not yet been verified by Armstrong.
              </p>
            ) : (
              <p className="text-[#8A6A00]">
                <Warning size={13} weight="fill" className="inline mr-1 mb-0.5" />
                No insurance certificate on file. We recommend confirming coverage with the provider directly before booking.
              </p>
            )}
          </div>
          <div>
            <p className="font-semibold text-ink text-[13px] mb-1.5">Venue disclosure</p>
            <p>
              Armstrong K9 Registry is a venue connecting you with <strong className="text-ink">{provider.business_name}</strong>, an independent business. The provider — not Armstrong K9 Registry — is responsible for performing this service. By booking, you agree Armstrong K9 Registry is not a party to the transaction between you and the provider.
            </p>
          </div>
        </div>
      </div>
      <BookingDialog
        open={bookOpen}
        onOpenChange={setBookOpen}
        provider={provider}
        services={provider.services || []}
      />
      <SchedulePicker
        open={!!schedFor}
        onOpenChange={(v) => { if (!v) setSchedFor(null); }}
        weeklyAvailability={sessionOffering?.weekly_availability || {}}
        durationMin={schedFor?.duration_min || 45}
        tierLabel={schedFor?.label || ""}
        onConfirm={submitBooking}
      />
    </PublicShell>
  );
}

function DisclRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[#F0F0F0] pb-2">
      <span className="text-[12px] uppercase tracking-wider text-[#888]">{label}</span>
      <span className="font-semibold text-[13px] text-right">{value}</span>
    </div>
  );
}

function labelize(s) {
  return String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

